import React, { useEffect, useState, useCallback } from 'react';
import {
  AnalyticalTable,
  Button,
  Dialog,
  Title,
  BusyIndicator,
  Input,
  Bar,
  Form,
  FormItem,
  FormGroup,
  MessageBox,
  MessageBoxType,
  MessageBoxAction,
  Text,
  Label
} from '@ui5/webcomponents-react';
import axiosInstance from '../utils/axiosInstance';
import usePermission from '../hooks/usePermission';
import withPermission from './withPermission';
import useAutoTranslate from '../hooks/useAutoTranslate';

const BrandingList = () => {
  const { hasPermission } = usePermission();
  const { autoT } = useAutoTranslate();

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ sortBy: 'name', sortDirection: 'asc' });

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedBranding, setSelectedBranding] = useState(null);

  const [formData, setFormData] = useState({ name: '', costCenter: '' });
  const [formErrors, setFormErrors] = useState({});

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const fetchBrandings = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`brandings?page=${page}&size=${pageSize}`);
      if (response.data.records.code === 'SUCCESS') {
        setData(response.data.records.data);
        setTotalPages(Math.ceil(response.data.totalCount / pageSize));
      } else {
        setError(response.data.records.message);
      }
    } catch (err) {
      setError('Error al cargar los brandings: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => { fetchBrandings(currentPage); }, [currentPage, fetchBrandings]);

  // ── Filtrado / Ordenamiento ────────────────────────────────────────────────

  useEffect(() => {
    let filtered = [...data];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(b =>
        (b.name || '').toLowerCase().includes(term) ||
        (b.costCenter || '').toLowerCase().includes(term)
      );
    }
    filtered.sort((a, b) => {
      const vA = a[sortConfig.sortBy] || '';
      const vB = b[sortConfig.sortBy] || '';
      const cmp = typeof vA === 'string'
        ? vA.localeCompare(vB)
        : vA - vB;
      return sortConfig.sortDirection === 'asc' ? cmp : -cmp;
    });
    setFilteredData(filtered);
  }, [data, searchTerm, sortConfig]);

  const handleSort = (col) =>
    setSortConfig(prev => ({
      sortBy: col,
      sortDirection: prev.sortBy === col && prev.sortDirection === 'asc' ? 'desc' : 'asc'
    }));

  // ── Paginación ─────────────────────────────────────────────────────────────

  const handlePrevPage = () => { if (currentPage > 1) setCurrentPage(p => p - 1); };
  const handleNextPage = () => { if (currentPage < totalPages) setCurrentPage(p => p + 1); };

  // ── Formulario ─────────────────────────────────────────────────────────────

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name || formData.name.trim() === '') {
      errors.name = autoT('El nombre es obligatorio');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Crear ──────────────────────────────────────────────────────────────────

  const handleCreateClick = () => {
    if (!hasPermission('branding', 'create')) return;
    setFormData({ name: '', costCenter: '' });
    setFormErrors({});
    setShowCreateDialog(true);
  };

  const handleCreateSubmit = async () => {
    if (!validateForm()) return;
    try {
      const response = await axiosInstance.post('brandings', formData);
      if (response.status === 200 || response.status === 201) {
        fetchBrandings(currentPage);
        setShowCreateDialog(false);
      } else {
        setError('Error al crear el branding: ' + (response.data?.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al crear el branding: ' + (err.message || 'Error desconocido'));
    }
  };

  // ── Editar ─────────────────────────────────────────────────────────────────

  const handleRowClick = (event) => {
    if (!hasPermission('branding', 'update')) return;
    const { detail } = event;
    if (detail?.row?.original) {
      const b = detail.row.original;
      setSelectedBranding(b);
      setFormData({ name: b.name || '', costCenter: b.costCenter || '' });
      setFormErrors({});
      setShowEditDialog(true);
    }
  };

  const handleEditClick = (branding) => {
    if (!hasPermission('branding', 'update')) return;
    setSelectedBranding(branding);
    setFormData({ name: branding.name || '', costCenter: branding.costCenter || '' });
    setFormErrors({});
    setShowEditDialog(true);
  };

  const handleEditSubmit = async () => {
    if (!validateForm() || !selectedBranding) return;
    try {
      const response = await axiosInstance.put(`brandings/${selectedBranding.id}`, formData);
      if (response.status === 200) {
        fetchBrandings(currentPage);
        setShowEditDialog(false);
      } else {
        setError('Error al actualizar el branding: ' + (response.data?.message || 'Error desconocido'));
      }
    } catch (err) {
      setError('Error al actualizar el branding: ' + (err.message || 'Error desconocido'));
    }
  };

  // ── Eliminar ───────────────────────────────────────────────────────────────

  const handleDeleteClick = (branding) => {
    if (!hasPermission('branding', 'delete')) return;
    setSelectedBranding(branding || selectedBranding);
    setShowDeleteConfirm(true);
    if (showEditDialog) setShowEditDialog(false);
  };

  const handleConfirmDelete = async () => {
    if (!selectedBranding) return;
    try {
      await axiosInstance.delete(`brandings/${selectedBranding.id}`);
      fetchBrandings(currentPage);
      setShowDeleteConfirm(false);
    } catch (err) {
      setError('Error al eliminar el branding: ' + (err.message || 'Error desconocido'));
    }
  };

  // ── Columnas ───────────────────────────────────────────────────────────────

  const columns = [
    {
      Header: autoT('Nombre'),
      accessor: 'name',
      headerProps: { onClick: () => handleSort('name') },
      className: sortConfig.sortBy === 'name' ? `sorted-${sortConfig.sortDirection}` : ''
    },
    {
      Header: autoT('Centro de Costo'),
      accessor: 'costCenter',
      headerProps: { onClick: () => handleSort('costCenter') },
      className: sortConfig.sortBy === 'costCenter' ? `sorted-${sortConfig.sortDirection}` : '',
      Cell: ({ value }) => <Text>{value || '—'}</Text>
    },
    {
      Header: autoT('Acciones'),
      id: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {hasPermission('branding', 'update') && (
            <Button icon="edit" design="Transparent"
              onClick={(e) => { e.stopPropagation(); handleEditClick(row.original); }} />
          )}
          {hasPermission('branding', 'delete') && (
            <Button icon="delete" design="Transparent"
              onClick={(e) => { e.stopPropagation(); handleDeleteClick(row.original); }} />
          )}
        </div>
      )
    }
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ padding: '1.5rem', backgroundColor: 'var(--sapBackgroundColor)', minHeight: '100vh' }}>
      <div style={{ padding: '1rem' }}>
        <Title level="H2">{autoT('Gestión de Branding')}</Title>

        {error && <div style={{ color: 'red', margin: '1rem 0' }}>{error}</div>}

        <Bar
          startContent={
            <Input
              placeholder={autoT('Buscar branding...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '250px' }}
            />
          }
          endContent={
            hasPermission('branding', 'create') && (
              <Button icon="add" design="Emphasized" onClick={handleCreateClick}>
                {autoT('Nuevo Branding')}
              </Button>
            )
          }
        />

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <BusyIndicator active size="Large" />
          </div>
        ) : (
          <>
            <AnalyticalTable
              data={filteredData}
              columns={columns}
              selectionMode="None"
              onRowClick={hasPermission('branding', 'update') ? handleRowClick : undefined}
              style={{ marginTop: '1rem' }}
              visibleRows={10}
              minRows={10}
              alternateRowColor
            />
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1rem' }}>
              <Button icon="navigation-left-arrow" disabled={currentPage === 1} onClick={handlePrevPage}>
                {autoT('Anterior')}
              </Button>
              <Text style={{ alignSelf: 'center' }}>
                {autoT('Página')} {currentPage} {autoT('de')} {totalPages}
              </Text>
              <Button icon="navigation-right-arrow" disabled={currentPage === totalPages} onClick={handleNextPage} iconEnd>
                {autoT('Siguiente')}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Dialog Crear */}
      <Dialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        header={<Bar startContent={<Title level="H4">{autoT('Crear Nuevo Branding')}</Title>} />}
        footer={
          <Bar endContent={
            <>
              <Button design="Emphasized" onClick={handleCreateSubmit}>{autoT('Guardar')}</Button>
              <Button design="Transparent" onClick={() => setShowCreateDialog(false)}>{autoT('Cancelar')}</Button>
            </>
          } />
        }
        style={{ width: '600px' }}
      >
        <Form style={{ padding: '1rem' }} backgroundDesign="Transparent"
          columnsXL={1} columnsL={1} columnsM={1} columnsS={1}
          labelSpanXL={4} labelSpanL={4} labelSpanM={12} labelSpanS={12}
        >
          <FormGroup titleText={autoT('Información del Branding')}>
            <FormItem labelContent={<Label required>{autoT('Nombre')}</Label>}>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                valueState={formErrors.name ? 'Error' : 'None'}
                valueStateMessage={formErrors.name}
                required
                style={{ width: '100%' }}
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Centro de Costo')}</Label>}>
              <Input
                name="costCenter"
                value={formData.costCenter}
                onChange={handleInputChange}
                style={{ width: '100%' }}
              />
            </FormItem>
          </FormGroup>
        </Form>
      </Dialog>

      {/* Dialog Editar */}
      <Dialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        header={<Bar startContent={<Title level="H4">{autoT('Editar Branding')}: {selectedBranding?.name}</Title>} />}
        footer={
          <Bar
            startContent={
              hasPermission('branding', 'delete') && (
                <Button design="Negative" icon="delete" onClick={() => handleDeleteClick(null)}>
                  {autoT('Eliminar')}
                </Button>
              )
            }
            endContent={
              <>
                <Button design="Emphasized" onClick={handleEditSubmit}>{autoT('Guardar Cambios')}</Button>
                <Button design="Transparent" onClick={() => setShowEditDialog(false)}>{autoT('Cancelar')}</Button>
              </>
            }
          />
        }
        style={{ width: '600px' }}
      >
        <Form style={{ padding: '1rem' }} backgroundDesign="Transparent"
          columnsXL={1} columnsL={1} columnsM={1} columnsS={1}
          labelSpanXL={4} labelSpanL={4} labelSpanM={12} labelSpanS={12}
        >
          <FormGroup titleText={autoT('Información del Branding')}>
            <FormItem labelContent={<Label required>{autoT('Nombre')}</Label>}>
              <Input
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                valueState={formErrors.name ? 'Error' : 'None'}
                valueStateMessage={formErrors.name}
                required
                style={{ width: '100%' }}
              />
            </FormItem>
            <FormItem labelContent={<Label>{autoT('Centro de Costo')}</Label>}>
              <Input
                name="costCenter"
                value={formData.costCenter}
                onChange={handleInputChange}
                style={{ width: '100%' }}
              />
            </FormItem>
          </FormGroup>
        </Form>
      </Dialog>

      {/* Confirmación eliminar */}
      <MessageBox
        open={showDeleteConfirm}
        onClose={(event) => {
          if (event.detail.action === MessageBoxAction.OK) {
            handleConfirmDelete();
          } else {
            setShowDeleteConfirm(false);
          }
        }}
        title={autoT('Confirmar eliminación')}
        type={MessageBoxType.WARNING}
        actions={[MessageBoxAction.OK, MessageBoxAction.CANCEL]}
      >
        {autoT('¿Está seguro que desea eliminar el branding')} <strong>{selectedBranding?.name}</strong>? {autoT('Esta acción no se puede deshacer.')}
      </MessageBox>
    </div>
  );
};

export default withPermission(BrandingList, 'branding', 'listView');
